import React, { Suspense } from "react";
import { motion } from "framer-motion";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, Environment, Torus, TorusKnot } from "@react-three/drei";

const LINE_1 = ["FORȚĂ.", "DISCIPLINĂ.", "ONOARE."];
const LINE_2 = ["ELITE CENTER"];
const LINE_3 = ["HUNEDOARA"];

const parent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};

const child = {
  hidden: { y: "110%" },
  show: {
    y: "0%",
    transition: { duration: 1.05, ease: [0.65, 0.05, 0.36, 1] },
  },
};

function SpinningEmblem() {
  const group = React.useRef();
  useFrame((state, delta) => {
    if (group.current) {
      group.current.rotation.y += delta * 0.25;
      group.current.rotation.x += delta * 0.08;
    }
  });
  return (
    <group ref={group}>
      <Float speed={1.3} rotationIntensity={0.35} floatIntensity={0.9}>
        <TorusKnot args={[1.05, 0.14, 220, 32, 2, 3]}>
          <meshStandardMaterial
            color={"#D91604"}
            metalness={0.85}
            roughness={0.18}
            emissive={"#4a0700"}
            emissiveIntensity={0.35}
          />
        </TorusKnot>
        <Torus args={[1.9, 0.02, 24, 160]} rotation={[Math.PI / 2, 0, 0]}>
          <meshStandardMaterial color={"#f2ebe5"} metalness={0.6} roughness={0.4} />
        </Torus>
        <Torus args={[2.35, 0.012, 24, 160]} rotation={[Math.PI / 2.4, 0.3, 0]}>
          <meshStandardMaterial color={"#8a8a8a"} metalness={0.5} roughness={0.5} />
        </Torus>
      </Float>
    </group>
  );
}

const KineticHero = () => {
  return (
    <section id="top" className="relative min-h-[100svh] w-full overflow-hidden" data-testid="hero-section">
      {/* 3D Canvas backdrop */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <Canvas
          camera={{ position: [0, 0, 5.6], fov: 42 }}
          gl={{ alpha: true, antialias: true }}
          dpr={[1, 1.6]}
        >
          <color attach="background" args={["#050505"]} />
          <ambientLight intensity={0.35} />
          <directionalLight position={[3, 4, 5]} intensity={1.1} color={"#ffffff"} />
          <directionalLight position={[-4, -2, 2]} intensity={0.6} color={"#D91604"} />
          <Suspense fallback={null}>
            <SpinningEmblem />
            <Environment preset="warehouse" />
          </Suspense>
          <fog attach="fog" args={["#050505", 6, 14]} />
        </Canvas>
      </div>

      {/* Vignette gradient */}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,rgba(5,5,5,0)_0%,rgba(5,5,5,0.35)_55%,#050505_100%)]" />

      {/* Content */}
      <div className="relative z-10 container-brutal px-6 md:px-12 pt-40 md:pt-48 pb-24 min-h-[100svh] grid grid-cols-12 gap-6">
        {/* Left vertical marker */}
        <div className="hidden md:flex col-span-1 items-end pb-16">
          <span className="vertical-text">Est. Hunedoara · Karate · MMA</span>
        </div>

        {/* Center content */}
        <div className="col-span-12 md:col-span-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="flex items-center gap-3 mb-8"
            data-testid="hero-eyebrow"
          >
            <span className="w-2 h-2 rounded-full bg-[#D91604] animate-pulse" />
            <span className="eyebrow">
              Sport Club · Karate Shotokan · Mixed Martial Arts
            </span>
          </motion.div>

          <motion.h1
            variants={parent}
            initial="hidden"
            animate="show"
            className="hero-title text-[#f2ebe5]"
            data-testid="hero-title"
          >
            <span className="line-mask">
              <motion.span variants={child} className="block">
                {LINE_1.map((w, i) => (
                  <span key={i} className="inline-block mr-4">
                    {i === 1 ? <span className="text-[#D91604]">{w}</span> : w}
                  </span>
                ))}
              </motion.span>
            </span>
            <span className="line-mask">
              <motion.span variants={child} className="block">
                {LINE_2}
              </motion.span>
            </span>
            <span className="line-mask">
              <motion.span variants={child} className="block">
                {LINE_3}
                <span className="text-[#D91604]">.</span>
              </motion.span>
            </span>
          </motion.h1>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-10 max-w-4xl">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.35 }}
              className="hero-sub md:col-span-8"
              data-testid="hero-subtitle"
            >
              Un dojo dedicat artei marțiale autentice. Antrenăm caracterul înainte de a
              antrena pumnul — sub îndrumarea antrenorului <em className="text-[#f2ebe5] not-italic">Șoș Eric, 3 Dan Shotokan</em>.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.6 }}
              className="md:col-span-8 -mt-2"
              data-testid="hero-affiliation-badge"
            >
              <div className="inline-flex items-center gap-4 border border-[#D91604] pl-3 pr-4 py-2.5 bg-[#D91604]/5">
                <div className="w-11 h-11 md:w-12 md:h-12 rounded-full overflow-hidden bg-white ring-1 ring-[#D91604]/30 shrink-0" data-testid="frk-logo">
                  <img
                    src="https://customer-assets.emergentagent.com/job_elite-karate-3d/artifacts/pe2bzf4w_Logo-FRK.jpg"
                    alt="Federația Română de Karate"
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-[11px] md:text-xs tracking-[0.22em] uppercase text-[#f2ebe5] leading-relaxed">
                  Membru oficial al Federației Române de Karate<br className="hidden md:inline" />
                  <span className="text-[#D91604]"> — unicul club afiliat din Hunedoara</span>
                </span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.55 }}
              className="md:col-span-4 flex flex-col items-start gap-3"
            >
              <a
                href="#program"
                data-testid="hero-cta-program"
                className="group inline-flex items-center gap-3 font-anton text-base tracking-widest uppercase px-5 py-3 bg-[#D91604] text-[#050505] hover:bg-[#f2ebe5] transition-colors"
              >
                Vezi programul
                <span className="inline-block group-hover:translate-x-1 transition-transform">→</span>
              </a>
              <a
                href="#contact"
                data-testid="hero-cta-visit"
                className="group inline-flex items-center gap-3 font-anton text-sm tracking-widest uppercase text-[#f2ebe5] hover:text-[#D91604] transition-colors"
              >
                Programează o ședință
                <span className="inline-block group-hover:translate-x-1 transition-transform">→</span>
              </a>
            </motion.div>
          </div>
        </div>

        {/* Right meta column */}
        <div className="hidden md:flex md:col-span-3 flex-col items-end justify-end pb-2 text-right gap-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.7 }}
          >
            <div className="eyebrow mb-2">Coord.</div>
            <div className="font-jp text-2xl text-[#f2ebe5]">45°45′N · 22°54′E</div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.85 }}
          >
            <div className="eyebrow mb-2">Dojo</div>
            <div className="font-editorial italic text-2xl leading-tight">
              Str. Victoriei 17,<br />Hunedoara
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 2, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-end gap-3"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
              className="w-24 h-24 lg:w-28 lg:h-28 rounded-full overflow-hidden bg-white ring-2 ring-[#D91604] shadow-[0_0_40px_rgba(217,22,4,0.35)]"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_elite-karate-3d/artifacts/17qa70pq_sigla%20club%202015-1.webp"
                alt="Elite Center Hunedoara"
                className="w-full h-full object-cover scale-[1.06]"
                data-testid="hero-logo"
              />
            </motion.div>
            <div className="eyebrow text-[#D91604] mt-2">押忍 · OSU · 道</div>
          </motion.div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2, duration: 0.6 }}
          className="col-span-12 flex items-center justify-between border-t border-[rgba(242,235,229,0.1)] pt-6 mt-16"
        >
          <span className="eyebrow">Scroll</span>
          <motion.span
            className="w-16 h-[1px] bg-[#f2ebe5]"
            animate={{ scaleX: [0, 1, 0] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
            style={{ transformOrigin: "left" }}
          />
          <span className="eyebrow font-jp">押忍 · JOS</span>
        </motion.div>
      </div>
    </section>
  );
};

export default KineticHero;
